import { CODE_WORLD_CATALOGUE, worldIn } from './world-catalogue.js';
export const PANO_LIMITS = {
    /** Distinct photos in a lesson, the scene's included. */
    perLesson: 4,
    /** Equirectangular: width / height = 2, within ±1 % (|w/h − 2| ≤ 0.02). */
    aspect: 2,
    aspectTolerance: 0.01,
    minWidth: 2048,
    /**
     * The width a photo is used at (its master): 8192 since 2 Oct 2026
     * (ADR-041), 5760 from 30 Sep, 3840 before. A wider photo is taken and
     * scaled to this by the media worker; the headset gets a 2048 px cube.
     */
    maxWidth: 8192,
    /** The widest photo taken as it is (an upload or a Commons download): Insta360 X4 is 11968. */
    inputMaxWidth: 12288,
    /** One upload or one Commons download, before the media worker makes its copies. */
    inputMaxBytes: 40 * 1024 * 1024,
    /**
     * The served JPEG's cap (`GET /v1/media/:id`): a master over it, or wider
     * than `jpegMaxWidth`, gets a 5760 px copy made by the media worker.
     */
    maxBytes: 16 * 1024 * 1024,
    /** The served JPEG's width at most: desktops, the console and older headsets take it. */
    jpegMaxWidth: 5760,
    wantMax: 200,
    searchMax: 80,
    searchWords: 6,
    creditMax: 140,
    findMax: 60,
    /** A brief carries at most this many school photos. */
    briefMax: 4,
};
/** What the platform fills in on a pano, never the model. */
export const PANO_FILLED_FIELDS = ['media', 'credit', 'yaw'];
/** What the platform fills in on a hotspot, never the model: where `find` points. */
export const HOTSPOT_FILLED_FIELDS = ['toward'];
export function isPanoLesson(lesson) {
    return lesson.scene.type === 'pano360';
}
/** A lesson on the 6 m stage (ADR-032): a pano, or a world that is staged (every one but the villa). */
export function isStagedLesson(lesson, worlds = CODE_WORLD_CATALOGUE) {
    if (lesson.scene.type === 'world') {
        // A world the catalogue does not have counts as staged, as it always did.
        return worldIn(worlds, lesson.scene.world)?.staged !== false;
    }
    return isPanoLesson(lesson);
}
function scenePhoto(lesson) {
    if (lesson.scene.type !== 'pano360')
        return undefined;
    const { type: _type, ...photo } = lesson.scene;
    return photo;
}
/** The scene's photo, then each step that sets one, in order. None outside a pano lesson. */
export function panoSlots(lesson) {
    const photo = scenePhoto(lesson);
    if (photo === undefined)
        return [];
    const slots = [{ slot: 'scene', photo }];
    for (const step of lesson.steps) {
        if (step.pano !== undefined)
            slots.push({ slot: `step:${step.id}`, photo: step.pano });
    }
    return slots;
}
/**
 * The photo in effect at step `index` (−1: before the first step): the last
 * `pano` at or before it, else the scene's. An index past the end is the last
 * step's. Undefined outside a pano lesson.
 */
export function panoAt(lesson, index) {
    const photo = scenePhoto(lesson);
    if (photo === undefined)
        return undefined;
    for (let i = Math.min(index, lesson.steps.length - 1); i >= 0; i--) {
        const step = lesson.steps[i];
        if (step.pano !== undefined)
            return { slot: `step:${step.id}`, photo: step.pano };
    }
    return { slot: 'scene', photo };
}
/** Equirectangular within ±1 %: the database's `abs(width − 2·height)·50 ≤ height`. */
export function isTwoToOne(width, height) {
    if (!(height > 0) || !(width > 0))
        return false;
    const slack = PANO_LIMITS.aspect * PANO_LIMITS.aspectTolerance * height;
    return Math.abs(width - PANO_LIMITS.aspect * height) <= slack + 1e-9 * height;
}
const RADIANS = Math.PI / 180;
/**
 * A unit direction in the room for a photo direction, with the photo turned
 * by `yaw`. `toward` is [azimuth, elevation] in the photo's own frame:
 * azimuth clockwise seen from above from the photo's centre column (pixel
 * column u = 0.5 + azimuth / 360), elevation above its horizon
 * (v = 0.5 − elevation / 180). The column at `yaw` is ahead of a pupil at the
 * start (−z); +x is to their right.
 */
export function towardDirection(toward, yaw) {
    const azimuth = (toward[0] - yaw) * RADIANS;
    const elevation = toward[1] * RADIANS;
    const flat = Math.cos(elevation);
    return [Math.sin(azimuth) * flat, Math.sin(elevation), -Math.cos(azimuth) * flat];
}
/**
 * The result key of a direction hotspot: `richting-1` for the step's first
 * hotspot. `hotspotIndex` is its index in the step's `hotspots`, from 0,
 * whatever the others are (the index the API points it by).
 */
export function directionKey(hotspotIndex) {
    return `richting-${hotspotIndex + 1}`;
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value, max) => typeof value === 'string' && value.trim().length > 0 && [...value].length <= max;
const wordCount = (text) => text.trim().split(/\s+/).length;
/** Which photo it is, for counting: by media, else brief, else the wish. */
function photoKey(photo) {
    const { media, brief, want, search } = photo;
    if (media !== undefined)
        return JSON.stringify(['media', media]);
    if (brief !== undefined)
        return JSON.stringify(['brief', brief]);
    return JSON.stringify(['want', want, search]);
}
const photosSent = (n) => n === 0 ? "geen foto's" : n === 1 ? '1 foto' : `${n} foto's`;
/**
 * The rules for a lesson's 360° photos (spec, "Semantic rules"), read from
 * the raw document so an answer the schema refused still says what broke:
 * one source per photo (`brief`, or `media`, or `want` with `search`;
 * `media` may keep its wish, `brief` stands alone; `credit` only with media,
 * `yaw` with media or brief; `search` 1–6 words); `step.pano` only in a
 * pano360 lesson; at most 4 distinct photos; a hotspot names exactly one of
 * `object`, `feature`, `toward`, `find` and `point`, the last three only in a pano360 lesson; a
 * pano360 lesson is VR. Whether `object` names an object stays the validator's rule, and whether
 * `feature` names a feature of the lesson's world is `assessWorldFeatures`' (ADR-087).
 */
export function assessPanos(document, options = {}) {
    const problems = [];
    if (!isRecord(document))
        return problems;
    const add = (path, en, nl) => void problems.push({ path, en, nl });
    const scene = isRecord(document['scene']) ? document['scene'] : {};
    const inPano = scene['type'] === 'pano360';
    const steps = Array.isArray(document['steps']) ? document['steps'] : [];
    const distinct = new Set();
    if (inPano && document['mode'] !== 'vr') {
        add('/mode', 'a pano360 lesson needs mode "vr"', "360°-foto's werken alleen in VR");
    }
    const judge = (raw, at, said) => {
        if (!isRecord(raw)) {
            add(at, 'a pano needs media, brief, or want with search', `${said}geen foto (want met search, of brief)`);
            return;
        }
        distinct.add(photoKey(raw));
        // "de foto aan het begin heeft geen zoekwoorden", but "stap "x": geen zoekwoorden".
        const has = at === '/scene' ? 'heeft ' : '';
        const media = raw['media'] !== undefined;
        const brief = raw['brief'] !== undefined;
        const wish = raw['want'] !== undefined || raw['search'] !== undefined;
        if (brief && (media || wish)) {
            add(at, 'a pano with brief takes no media, want or search', `${said}heeft brief én ${media ? 'media' : 'want of search'} (brief staat alleen)`);
        }
        else if (!brief && !media) {
            if (!isText(raw['want'], PANO_LIMITS.wantMax)) {
                add(at, `a pano needs media, brief, or want (1–${PANO_LIMITS.wantMax} characters) with search`, `${said}zegt niet welke foto (want, 1–${PANO_LIMITS.wantMax} tekens)`);
            }
            if (!isText(raw['search'], PANO_LIMITS.searchMax)) {
                add(at, `a pano with want needs search (1–${PANO_LIMITS.searchMax} characters)`, `${said}${has}geen zoekwoorden (search, 1–${PANO_LIMITS.searchMax} tekens)`);
            }
        }
        const search = raw['search'];
        if (isText(search, PANO_LIMITS.searchMax) && wordCount(search) > PANO_LIMITS.searchWords) {
            const count = wordCount(search);
            add(`${at}/search`, `${count} search words; at most ${PANO_LIMITS.searchWords}`, `${said}${has}${count} zoekwoorden (1–${PANO_LIMITS.searchWords})`);
        }
        const n = raw['brief'];
        const sent = options.briefPhotos;
        if (typeof n === 'number' && sent !== undefined && n > sent) {
            add(`${at}/brief`, `photo ${n} of the brief does not exist (the brief has ${sent})`, `${said}foto ${n} van de school bestaat niet (de school stuurde ${photosSent(sent)})`);
        }
        if (raw['credit'] !== undefined && !media) {
            add(`${at}/credit`, 'credit goes only with media', `${said}heeft credit zonder media`);
        }
        if (raw['yaw'] !== undefined && !media && !brief) {
            add(`${at}/yaw`, 'yaw goes only with media or brief', `${said}heeft yaw zonder media of brief`);
        }
        if (options.generated && PANO_FILLED_FIELDS.some((field) => raw[field] !== undefined)) {
            add(at, 'media, credit and yaw are filled in by the platform', `${said}heeft zelf media, credit of yaw (dat vult het platform in)`);
        }
    };
    if (inPano)
        judge(scene, '/scene', 'de foto aan het begin ');
    steps.forEach((raw, index) => {
        if (!isRecord(raw))
            return;
        const at = `/steps/${index}`;
        const said = `stap "${typeof raw['id'] === 'string' ? raw['id'] : index + 1}": `;
        if (raw['pano'] !== undefined) {
            if (inPano)
                judge(raw['pano'], `${at}/pano`, said);
            else {
                add(`${at}/pano`, '"pano" goes only in a pano360 lesson', `${said}"pano" kan alleen in een les met een 360°-foto`);
            }
        }
        const hotspots = Array.isArray(raw['hotspots']) ? raw['hotspots'] : [];
        hotspots.forEach((hotspot, h) => {
            if (!isRecord(hotspot))
                return;
            const where = `${at}/hotspots/${h}`;
            const which = `${said}hotspot ${h + 1} `;
            const named = ['object', 'feature', 'toward', 'find', 'point'].filter((field) => hotspot[field] !== undefined);
            if (named.length !== 1) {
                add(where, 'a hotspot names exactly one of object, feature, toward, find and point', named.length === 0
                    ? `${which}noemt geen object${inPano ? ' of find' : ''}`
                    : `${which}noemt ${named.join(' én ')}`);
            }
            const direction = named.filter((field) => field !== 'object' && field !== 'feature');
            if (direction.length > 0 && !inPano) {
                add(where, 'find, point and toward go only in a pano360 lesson', `${which}heeft ${direction.join(' en ')}, wat alleen in een les met een 360°-foto kan`);
            }
            else if (options.generated && hotspot['toward'] !== undefined) {
                add(`${where}/toward`, 'toward is filled in by the platform', `${which}heeft toward (dat wijst het platform aan)`);
            }
        });
    });
    if (distinct.size > PANO_LIMITS.perLesson) {
        add('/steps', `${distinct.size} distinct 360° photos; a lesson may have at most ${PANO_LIMITS.perLesson}`, `de les heeft ${distinct.size} 360°-foto's (hooguit ${PANO_LIMITS.perLesson})`);
    }
    return problems;
}
//# sourceMappingURL=panos.js.map